import React, { useState, useEffect } from "react";
import useInputState from "../../hooks/useInputState";
import {
  InvalidCardNumberError,
  InvalidCardCvvError,
  InvalidCardExpirationError,
  InvalidCardholderNameError,
  InvalidCardError
} from "../../helpers/StripeHelper";
import stripeHelper from "../../helpers/StripeHelper";
import usePreviousValue from "../../hooks/usePreviousValue";
import { useMutation } from "@apollo/react-hooks";
import { updatePaymentInfo } from "./mutation";
import DefaultToaster from "../../atoms/DefaultToaster";
import { GetSubscriptionDetails } from "../../pages/Settings/BillingInformationSettings/queries";

const getError = () => {
  return {
    name: InvalidCardError,
    title: "Card Update Failed",
    message: (
      <span>
        Something went wrong! Try again or contact{" "}
        {
          <a
            href="https://help.videosquirrel.app/"
            target="_blank"
            rel="noopener noreferrer"
          >
            support
          </a>
        }{" "}
        if the problem persists.
      </span>
    )
  };
};

export default function usePaymentSidebar({ onDismiss }) {
  const [loading, setLoading] = useState(false);
  const [cardNumber, setCardNumber] = useInputState("");
  const [cardholderName, setCardHolderName] = useInputState("");
  const [expiration, setExpiration] = useInputState("");
  const [cvv, setCvv] = useInputState("");
  const [error, setError] = useState({});
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  const [updateCard] = useMutation(updatePaymentInfo, {
    refetchQueries: [{ query: GetSubscriptionDetails }],
    awaitRefetchQueries: true
  });

  const isCardNumberValid =
    error.name === InvalidCardNumberError
      ? false
      : cardNumber.length >= 12
      ? true
      : null;
  const isCardNameValid =
    error.name === InvalidCardholderNameError
      ? false
      : !!cardholderName
      ? true
      : null;

  const isExpirationDateValid =
    error.name === InvalidCardExpirationError
      ? false
      : expiration.length === 5
      ? true
      : null;

  const isCvvValid =
    error.name === InvalidCardCvvError
      ? false
      : cvv.length === 3 || cvv.length === 4
      ? true
      : null;

  const isCardError = error.name === InvalidCardError;

  // Remove error on change in field

  const prevCardNumber = usePreviousValue(cardNumber);
  const prevCardholderName = usePreviousValue(cardholderName);
  const prevExpiration = usePreviousValue(expiration);
  const prevCvv = usePreviousValue(cvv);

  useEffect(() => {
    prevCardNumber !== cardNumber &&
      isCardNumberValid === false &&
      setError({});
    cardholderName !== prevCardholderName &&
      isCardNameValid === false &&
      setError({});
    expiration !== prevExpiration &&
      isExpirationDateValid === false &&
      setError({});
    cvv !== prevCvv && isCvvValid === false && setError({});
  }, [
    cardNumber,
    prevCardNumber,
    isCardNumberValid,
    cardholderName,
    prevCardholderName,
    isCardNameValid,
    expiration,
    prevExpiration,
    isExpirationDateValid,
    cvv,
    prevCvv,
    isCvvValid
  ]);

  const onAlertCancel = () => {
    setIsAlertOpen(false);
  };

  const onDismissSidebar = () => {
    setIsAlertOpen(true);
  };

  const resetForm = () => {
    setCardNumber("");
    setCardHolderName("");
    setExpiration("");
    setCvv("");
    setError({});
  };

  const onAlertConfirm = () => {
    setLoading(false);
    resetForm();
    setIsAlertOpen(false);
    onDismiss();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError({});
    setLoading(true);

    const data = {
      cardNumber,
      cardholderName,
      cvv,
      expiration
    };
    try {
      const response = await stripeHelper.createTokenFromCard(data);
      const body = await response.json();

      const tokenId = body.id;

      const {
        data: { updatePaymentInfo: status }
      } = await updateCard({
        variables: {
          tokenId
        }
      });
      if (status == "Updated") {
        resetForm();
        onDismiss();
        DefaultToaster.show({
          message: "Card updated successfully",
          icon: "tick-circle",
          timeout: 4000
        });
      } else {
        setError(getError());
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  return {
    onDismissSidebar,
    onAlertCancel,
    onAlertConfirm,
    isAlertOpen,
    cardNumber,
    setCardNumber,
    cardholderName,
    setCardHolderName,
    expiration,
    setExpiration,
    cvv,
    setCvv,
    error,
    isCardNumberValid,
    isCardNameValid,
    isExpirationDateValid,
    isCvvValid,
    isCardError,
    isFormValid:
      isCardNumberValid &&
      isCardNameValid &&
      isExpirationDateValid &&
      isCvvValid,
    onSubmit,
    loading
  };
}
